"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { AlertCircleIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { RERUN_INDEX_WORKER_JOB_NAME } from "./constants";
import type { RerunIndexWorkerJobInput } from "./schema";

const RerunIndexWorkerJobConfirmQuery = graphql(`
  query RerunIndexWorkerJobConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on IndexWorker {
        id
        name
        schedule
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

/**
 * Index Worker 再実行の確認 UI。
 */
export function RerunIndexWorkerJobConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const router = useRouter();
  const toolName = isToolUIPart(part) ? getToolName(part) : null;
  const isTarget = toolName === RERUN_INDEX_WORKER_JOB_NAME;
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ?? {}) as Partial<RerunIndexWorkerJobInput>;
  const indexWorkerId = input.indexWorkerId ?? "";

  const [{ data, fetching, error }] = useQuery({
    query: RerunIndexWorkerJobConfirmQuery,
    variables: { id: indexWorkerId },
    pause: !isTarget || !isInputAvailable || !indexWorkerId,
  });

  if (!isTarget || !isInputAvailable) {
    return null;
  }

  const worker = data?.node?.__typename === "IndexWorker" ? data.node : null;
  const pending = fetching && !worker;
  const loadFailed = !!error || (!pending && !worker);

  return (
    <div className="p-4 text-sm space-y-4">
      <div>
        <div className="mb-2 font-medium">
          <Trans>Do you want to rerun this Index Worker job once?</Trans>
        </div>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <Trans>Name</Trans>:{" "}
            {pending ? (
              <Skeleton className="inline-block h-4 w-36 align-middle" />
            ) : (
              <span className="font-medium">{worker?.name ?? "-"}</span>
            )}
          </li>
          <li>
            <Trans>Schedule</Trans>:{" "}
            {pending ? (
              <Skeleton className="inline-block h-4 w-20 align-middle" />
            ) : (
              <span className="font-medium">{worker?.schedule ?? "-"}</span>
            )}
          </li>
        </ul>
      </div>

      {loadFailed ? (
        <Alert variant="destructive" className="text-xs">
          <AlertTitle>
            <Trans>Unable to load the Index Worker</Trans>
          </AlertTitle>
          <AlertDescription>
            <Trans>
              It may have been deleted or is inaccessible. Please reload and try
              again.
            </Trans>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="text-xs">
          <AlertCircleIcon className="h-4 w-4" />
          <AlertTitle>
            <Trans>One-time execution</Trans>
          </AlertTitle>
          <AlertDescription>
            <Trans>
              This runs the Index Worker once immediately and keeps the current
              schedule unchanged.
            </Trans>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2">
        {loadFailed ? (
          <Button size="sm" variant="secondary" disabled>
            <Trans>Run</Trans>
          </Button>
        ) : (
          <ApproveButton
            toolCallId={part.toolCallId}
            toolName={toolName ?? RERUN_INDEX_WORKER_JOB_NAME}
            addToolResult={addToolResult}
            sendMessage={sendMessage}
            onSuccess={() => router.refresh()}
          >
            <Trans>Run</Trans>
          </ApproveButton>
        )}
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={toolName ?? RERUN_INDEX_WORKER_JOB_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
